import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import {
  DevHubBox,
  DevHubTypography,
  DevHubTextField,
  DevHubMenuItem,
} from '@devxdiscover/devhub-ui';

interface FunctionalAreaSelectorProps {
  functionalAreas: string[];
  selectedFunctionalArea: string;
  onSelectionChange: (value: string) => void;
  isLoadingFunctionalAreas?: boolean;
}

// Stable style objects to prevent re-renders
const boxStyle = { marginBottom: '1.5rem' };
const singleAreaBoxStyle = { marginBottom: '1rem' };
const singleAreaTextStyle = { color: '#666' };
const textFieldStyle = { minWidth: '350px', maxWidth: '500px', margin: 0 };
const inputStyle = { margin: 0 };

/**
 * Functional Area Selector Component
 * @param param0 - The props for the component
 * @returns The rendered component
 */
export const FunctionalAreaSelector: FC<FunctionalAreaSelectorProps> = ({
  functionalAreas,
  selectedFunctionalArea,
  onSelectionChange,
  isLoadingFunctionalAreas = false,
}) => {
  const handleChange = useCallback(
    (event: any) => {
      onSelectionChange(event.target.value);
    },
    [onSelectionChange],
  );

  const helperText = useMemo(
    () => `${functionalAreas.length} areas available`,
    [functionalAreas.length],
  );

  const sortedAreas = useMemo(
    () => functionalAreas.slice().sort((a, b) => a.localeCompare(b)),
    [functionalAreas],
  );

  if (isLoadingFunctionalAreas) {
    return (
      <DevHubTypography style={{ color: '#1565c0' }}>
        Loading functional areas...
      </DevHubTypography>
    );
  }

  if (functionalAreas.length === 0) {
    return (
      <DevHubTypography color="error">
        You are not a Data Business Owner for any functional areas.
      </DevHubTypography>
    );
  }

  if (functionalAreas.length === 1) {
    return (
      <DevHubBox style={singleAreaBoxStyle}>
        <DevHubTypography variant="subtitle1" style={singleAreaTextStyle}>
          Managing Data Leads for: <strong>{functionalAreas[0]}</strong>
        </DevHubTypography>
      </DevHubBox>
    );
  }

  return (
    <DevHubBox style={boxStyle}>
      <DevHubTextField
        select
        value={selectedFunctionalArea}
        onChange={handleChange}
        label="Functional Area"
        variant="outlined"
        size="small"
        helperText={helperText}
        style={textFieldStyle}
        InputLabelProps={{
          htmlFor: 'functional-area-selector',
        }}
        InputProps={{
          id: 'functional-area-selector',
          style: inputStyle,
        }}
        inputProps={{
          'aria-label': 'Functional Area',
          'aria-describedby': 'functional-area-helper-text',
        }}
        FormHelperTextProps={{
          id: 'functional-area-helper-text',
        }}
      >
        {sortedAreas.map(area => (
          <DevHubMenuItem key={area} value={area}>
            {area}
          </DevHubMenuItem>
        ))}
      </DevHubTextField>
    </DevHubBox>
  );
};

export default FunctionalAreaSelector;
